import { motion } from "framer-motion";
import { Phone, Mail } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import RevealText from "@/components/RevealText";
import {
  CONTACT_PHONE,
  CONTACT_PHONE_HREF,
  CONTACT_EMAIL,
  CONTACT_EMAIL_HREF,
} from "@/components/Nav";

export default function Contact() {
  useSEO({
    title: "Contact — Manitoba Security Services / Maze Security Solutions",
    description:
      "Contact Maze Security Solutions and Manitoba Security Services by phone or email. Coverage across every community in Manitoba — mining, mobile patrol, event, corporate, fire watch, and more.",
    keywords: [
      "contact Manitoba Security Services",
      "Maze Security Solutions contact",
      "Manitoba security phone",
      "Manitoba security email",
      "security guard services contact Manitoba",
    ],
    canonical:
      typeof window !== "undefined"
        ? window.location.origin + "/contact"
        : "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact — Manitoba Security Services",
      mainEntity: {
        "@type": "Organization",
        name: "Maze Security Solutions / Manitoba Security Services",
        telephone: "+1-807-907-2874",
        email: "info@mazesecurity.ca",
        areaServed: { "@type": "AdministrativeArea", name: "Manitoba" },
      },
    },
  });

  return (
    <div className="bg-[#050505] pt-40 md:pt-48">
      {/* Header */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-16 md:pb-24 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#FF3B30] text-sm">
            Index — 07
          </p>
        </div>
        <div className="col-span-12 md:col-span-9">
          <RevealText
            lines={["Contact."]}
            className="font-display font-black uppercase tracking-tightest leading-[0.88] text-white text-5xl md:text-7xl lg:text-8xl"
            delay={0.05}
          />
          <p className="mt-8 font-body text-base md:text-lg text-[#a0a0a0] max-w-2xl leading-relaxed">
            Coverage across every community in Manitoba. Reach us directly by
            phone or email — no forms, no delays.
          </p>
        </div>
      </section>

      {/* Contact cards */}
      <section className="border-t border-[#1e1e1e]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          <motion.a
            href={CONTACT_PHONE_HREF}
            data-testid="contact-page-phone"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="group relative border border-[#1e1e1e] hover:border-[#FF3B30] p-8 md:p-12 transition-colors"
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="inline-flex h-10 w-10 items-center justify-center border border-[#FF3B30] text-[#FF3B30] group-hover:bg-[#FF3B30] group-hover:text-[#050505] transition-colors">
                <Phone size={18} strokeWidth={2.2} />
              </span>
              <p className="font-editorial italic text-[#FF3B30] text-sm">
                Phone
              </p>
            </div>
            <p className="font-display font-black uppercase tracking-tight text-white text-3xl md:text-5xl leading-none group-hover:translate-x-1 transition-transform">
              {CONTACT_PHONE}
            </p>
            <p className="mt-4 font-body text-sm text-[#a0a0a0] uppercase tracking-[0.14em]">
              Call for coverage anywhere in Manitoba
            </p>
          </motion.a>

          <motion.a
            href={CONTACT_EMAIL_HREF}
            data-testid="contact-page-email"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="group relative border border-[#1e1e1e] hover:border-[#FF3B30] p-8 md:p-12 transition-colors"
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="inline-flex h-10 w-10 items-center justify-center border border-[#FF3B30] text-[#FF3B30] group-hover:bg-[#FF3B30] group-hover:text-[#050505] transition-colors">
                <Mail size={18} strokeWidth={2.2} />
              </span>
              <p className="font-editorial italic text-[#FF3B30] text-sm">
                Email
              </p>
            </div>
            <p className="font-display font-black uppercase tracking-tight text-white text-2xl md:text-4xl leading-none break-all group-hover:translate-x-1 transition-transform">
              {CONTACT_EMAIL}
            </p>
            <p className="mt-4 font-body text-sm text-[#a0a0a0] uppercase tracking-[0.14em]">
              Email for quotes, RFPs, and coverage requests
            </p>
          </motion.a>
        </div>
      </section>

      {/* Coverage note */}
      <section className="border-t border-[#1e1e1e] bg-[#0b0b0b]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-28 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-4">
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              — Coverage
            </p>
            <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-3xl md:text-4xl leading-tight">
              Province-wide
            </h2>
          </div>
          <div className="col-span-12 md:col-span-8 space-y-6">
            <p className="font-body text-base md:text-lg text-[#a0a0a0] leading-[1.75]">
              Maze Security Solutions and Manitoba Security Services deliver
              guard coverage across every community in Manitoba — from downtown
              Winnipeg to the fly-in mining camps of Thompson, from the oil and
              gas footprint of Melita to the Whiteshell approach at Lac du
              Bonnet.
            </p>
            <p className="font-body text-base md:text-lg text-[#a0a0a0] leading-[1.75]">
              Mining, mobile patrol, event, corporate, construction, retail loss
              prevention, residential, executive protection, concierge, alarm
              response, warehouse and industrial, oil and gas, healthcare,
              education, and fire watch security — one operating standard,
              executed province-wide.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
